import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import { COLORS, API } from "../constants";
import FilePreviewModal from "../components/FilePreviewModal";

export default function GroupsPage() {
  const {
    groups, pendingInvites, activeGroup, setActiveGroup, groupMembers,
    loadingGroups, creatingGroup, groupError, setGroupError,
    inviteEmail, setInviteEmail, invitingMember,
    fetchGroups, fetchPendingInvites, fetchGroupMembers,
    handleCreateGroup, handleInviteMember, handleAcceptInvite, handleDeclineInvite, handleLeaveGroup,
    handlePromoteMember, handleRemoveMember,
    groupPosts, loadingPosts, creatingPost, postError, setPostError,
    fetchGroupPosts, handleCreatePost,
    postDetail, loadingPostDetail, addingComment, commentError, setCommentError,
    fetchPostDetail, clearPostDetail, handleAddComment,
    sharedFiles, loadingSharedFiles, groupFolders, copyingFileId, sharedFilesMessage, setSharedFilesMessage,
    fetchSharedFiles, fetchGroupFolders, handleCreateGroupFolder, handleAssignSharedFileFolder, handleCopyToMyFiles,
    summarizingSharedFileIds, deletingSharedFileId, downloadingSharedZip,
    handleSummarizeSharedFile, handleDeleteSharedFile, handleDownloadSharedZip,
    currentUserId, token
  } = useApp();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostFile, setNewPostFile] = useState(null);
  const [activePost, setActivePost] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentFile, setCommentFile] = useState(null);
  const [commentVersionOf, setCommentVersionOf] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [activeTab, setActiveTab] = useState("discussion"); // "discussion" | "files"
  const [activeWallFolderId, setActiveWallFolderId] = useState(null);
  const [showNewWallFolderForm, setShowNewWallFolderForm] = useState(false);
  const [newWallFolderName, setNewWallFolderName] = useState("");
  const [scrollToCommentId, setScrollToCommentId] = useState(null);
  const threadRef = useRef(null);
  const [sharedFileContextMenu, setSharedFileContextMenu] = useState(null);
  const [expandedSharedSummaries, setExpandedSharedSummaries] = useState({});

  useEffect(() => {
    fetchGroups();
    fetchPendingInvites();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeGroup) {
      fetchGroupMembers(activeGroup.id);
      fetchGroupPosts(activeGroup.id);
      fetchGroupFolders(activeGroup.id);
      setActivePost(null);
      clearPostDetail();
      setActiveTab("discussion");
      setActiveWallFolderId(null);
    }
  }, [activeGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeGroup && activePost) {
      fetchPostDetail(activeGroup.id, activePost);
    }
  }, [activePost]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeGroup && activeTab === "files") {
      fetchSharedFiles(activeGroup.id);
    }
  }, [activeGroup, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to and briefly highlight a comment once the thread has rendered
  useEffect(() => {
    if (!scrollToCommentId || !postDetail) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(scrollToCommentId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "background 0.3s";
        const original = el.style.background;
        el.style.background = COLORS.blueMid;
        setTimeout(() => { el.style.background = original; }, 1500);
      }
      setScrollToCommentId(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [scrollToCommentId, postDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitCreateGroup = async () => {
    await handleCreateGroup(newGroupName);
    setNewGroupName("");
    setShowCreateForm(false);
  };

  const submitNewPost = async () => {
    await handleCreatePost(activeGroup.id, newPostTitle, newPostFile);
    setNewPostTitle("");
    setNewPostFile(null);
    setShowNewPostForm(false);
  };

  const openPost = (postId) => {
    setActiveTab("discussion");
    setActivePost(postId);
  };

  const backToFeed = () => {
    setActivePost(null);
    clearPostDetail();
    setCommentText(""); setCommentFile(null); setCommentVersionOf("");
    setCommentError("");
  };

  const submitComment = async () => {
    await handleAddComment(activeGroup.id, activePost, commentText, commentFile, commentVersionOf || null);
    setCommentText(""); setCommentFile(null); setCommentVersionOf("");
  };

  const filesInThread = () => {
    if (!postDetail) return [];
    const seen = new Map();
    if (postDetail.file) seen.set(postDetail.file.id, postDetail.file);
    postDetail.comments.forEach(c => { if (c.file) seen.set(c.file.id, c.file); });
    return Array.from(seen.values());
  };

  const jumpToFileInThread = (file) => {
    openPost(file.post_id);
    setScrollToCommentId(file.comment_id ? `comment-${file.comment_id}` : `post-original`);
  };

  const submitNewWallFolder = async () => {
    await handleCreateGroupFolder(activeGroup.id, newWallFolderName);
    setNewWallFolderName("");
    setShowNewWallFolderForm(false);
  };

  // Flatten group folder tree into a simple list for the assignment dropdown
  const flattenFolders = (list, depth = 0) => {
    let out = [];
    for (const f of list) {
      out.push({ id: f.id, name: f.name, depth });
      if (f.children?.length) out = out.concat(flattenFolders(f.children, depth + 1));
    }
    return out;
  };
  const flatWallFolders = flattenFolders(groupFolders);

  const visibleWallFiles = activeWallFolderId
    ? sharedFiles.filter(f => f.folder_id === activeWallFolderId)
    : sharedFiles;

  const activeWallFolderName = activeWallFolderId
    ? flatWallFolders.find(f => f.id === activeWallFolderId)?.name || "folder"
    : "all_shared_files";

  const myMembership = groupMembers.find(m => m.user_id === currentUserId);
  const isAdmin = myMembership?.role === "admin";

  return (
    <div style={{ padding: "32px", maxWidth: "1200px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: "700", color: COLORS.textPrimary, margin: 0 }}>Groups</h1>
        <button onClick={() => setShowCreateForm(true)}
          style={{ padding: "8px 16px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500" }}>
          + New Group
        </button>
      </div>

      {groupError && (
        <div style={{ marginBottom: "16px", padding: "10px 14px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "13px", color: COLORS.danger }}>{groupError}</span>
          <span onClick={() => setGroupError("")} style={{ cursor: "pointer", fontSize: "14px", color: COLORS.danger }}>✕</span>
        </div>
      )}

      {/* Create Group Form */}
      {showCreateForm && (
        <div style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "18px", marginBottom: "20px" }}>
          <label style={{ fontSize: "12px", color: COLORS.textSecondary, display: "block", marginBottom: "6px" }}>Group name</label>
          <input
            type="text"
            placeholder="e.g. CINE 110 Instructors"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submitCreateGroup()}
            autoFocus
            style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none", boxSizing: "border-box", marginBottom: "12px" }}
          />
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button onClick={() => { setShowCreateForm(false); setNewGroupName(""); }}
              style={{ padding: "8px 16px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>
              Cancel
            </button>
            <button onClick={submitCreateGroup} disabled={creatingGroup || !newGroupName.trim()}
              style={{ padding: "8px 20px", fontSize: "13px", background: creatingGroup || !newGroupName.trim() ? COLORS.bgTertiary : COLORS.blue, color: creatingGroup || !newGroupName.trim() ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: creatingGroup || !newGroupName.trim() ? "default" : "pointer", fontWeight: "500" }}>
              {creatingGroup ? "Creating..." : "Create Group"}
            </button>
          </div>
        </div>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "8px" }}>Pending Invites</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {pendingInvites.map(invite => (
              <div key={invite.group_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#FEF9F0", border: "0.5px solid #FDE68A", borderRadius: "9px" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary }}>{invite.group_name}</div>
                  <div style={{ fontSize: "12px", color: COLORS.textTertiary }}>Invited by {invite.invited_by}</div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => handleAcceptInvite(invite.group_id)}
                    style={{ padding: "6px 14px", fontSize: "12px", background: COLORS.blue, color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>
                    Accept
                  </button>
                  <button onClick={() => handleDeclineInvite(invite.group_id)}
                    style={{ padding: "6px 14px", fontSize: "12px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: COLORS.textSecondary, cursor: "pointer" }}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Groups + Group Detail */}
      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

        {/* Group List */}
        <div style={{ width: "260px", flexShrink: 0 }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "8px" }}>My Groups</h3>
          {loadingGroups ? (
            <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading...</p>
          ) : groups.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px" }}>
              <p style={{ fontSize: "14px", color: COLORS.textSecondary, marginBottom: "4px" }}>No groups yet</p>
              <p style={{ fontSize: "12px", color: COLORS.textTertiary }}>Create a group to start sharing content with co-instructors</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {groups.map(g => (
                <div key={g.id} onClick={() => setActiveGroup(g)}
                  style={{ padding: "12px 16px", background: activeGroup?.id === g.id ? COLORS.blueLight : COLORS.bg, border: `0.5px solid ${activeGroup?.id === g.id ? COLORS.blue : COLORS.border}`, borderRadius: "9px", cursor: "pointer" }}>
                  <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary }}>{g.name}</div>
                  <div style={{ fontSize: "12px", color: COLORS.textTertiary }}>{g.member_count} member{g.member_count !== 1 ? "s" : ""} · {g.role === "admin" ? "Admin" : "Member"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Group Detail: Members + Discussion/Files */}
        {activeGroup && (
          <div style={{ flex: 1, display: "flex", gap: "20px", alignItems: "flex-start" }}>

            {/* Members panel */}
            <div style={{ width: "280px", flexShrink: 0, background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <h3 style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, margin: 0 }}>{activeGroup.name}</h3>
                <span onClick={() => setActiveGroup(null)} style={{ cursor: "pointer", fontSize: "14px", color: COLORS.textTertiary }}>✕</span>
              </div>

              <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
                <input
                  type="email"
                  placeholder="Invite by email..."
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleInviteMember(activeGroup.id)}
                  style={{ flex: 1, padding: "7px 10px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none" }}
                />
                <button onClick={() => handleInviteMember(activeGroup.id)} disabled={invitingMember || !inviteEmail.trim()}
                  style={{ padding: "7px 14px", fontSize: "13px", background: invitingMember || !inviteEmail.trim() ? COLORS.bgTertiary : COLORS.blue, color: invitingMember || !inviteEmail.trim() ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: invitingMember || !inviteEmail.trim() ? "default" : "pointer", fontWeight: "500", whiteSpace: "nowrap" }}>
                  {invitingMember ? "Inviting…" : "Invite"}
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
                {groupMembers.map(m => (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: "7px", background: COLORS.bgSecondary }}>
                    <div>
                      <div style={{ fontSize: "13px", color: COLORS.textPrimary }}>{m.name}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textTertiary }}>{m.role === "admin" ? "Admin" : "Member"}{m.status === "pending" ? " · Pending" : ""}</div>
                    </div>
                    {isAdmin && m.user_id !== currentUserId && (
                      <div style={{ display: "flex", gap: "6px" }}>
                        {m.role !== "admin" && (
                          <span onClick={() => handlePromoteMember(activeGroup.id, m.user_id)} title="Promote to admin" style={{ cursor: "pointer", fontSize: "12px", color: COLORS.blue }}>⬆️</span>
                        )}
                        <span onClick={() => handleRemoveMember(activeGroup.id, m.user_id, m.name)} title="Remove from group" style={{ cursor: "pointer", fontSize: "12px", color: COLORS.danger }}>✕</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button onClick={() => handleLeaveGroup(activeGroup.id)}
                style={{ width: "100%", padding: "8px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "7px", color: COLORS.danger, cursor: "pointer" }}>
                Leave Group
              </button>
            </div>

            {/* Discussion / Shared Files */}
            <div style={{ flex: 1 }}>

              {/* Tab switcher */}
              <div style={{ display: "flex", gap: "4px", marginBottom: "16px", borderBottom: `0.5px solid ${COLORS.border}` }}>
                <div onClick={() => { setActiveTab("discussion"); }}
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "500", cursor: "pointer", color: activeTab === "discussion" ? COLORS.blue : COLORS.textSecondary, borderBottom: activeTab === "discussion" ? `2px solid ${COLORS.blue}` : "2px solid transparent" }}>
                  Discussion
                </div>
                <div onClick={() => { setActiveTab("files"); setActivePost(null); clearPostDetail(); }}
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "500", cursor: "pointer", color: activeTab === "files" ? COLORS.blue : COLORS.textSecondary, borderBottom: activeTab === "files" ? `2px solid ${COLORS.blue}` : "2px solid transparent" }}>
                  Shared Files
                </div>
              </div>

              {activeTab === "discussion" && (
                !activePost ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                      <h3 style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, margin: 0 }}>Discussion</h3>
                      <button onClick={() => setShowNewPostForm(true)}
                        style={{ padding: "7px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "7px", cursor: "pointer", fontWeight: "500" }}>
                        + New Post
                      </button>
                    </div>

                    {postError && (
                      <div style={{ marginBottom: "12px", padding: "8px 12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: "12px", color: COLORS.danger }}>{postError}</span>
                        <span onClick={() => setPostError("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.danger }}>✕</span>
                      </div>
                    )}

                    {showNewPostForm && (
                      <div style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "16px", marginBottom: "14px" }}>
                        <input
                          type="text"
                          placeholder="Post title..."
                          value={newPostTitle}
                          onChange={e => setNewPostTitle(e.target.value)}
                          autoFocus
                          style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none", boxSizing: "border-box", marginBottom: "10px" }}
                        />
                        <input
                          type="file"
                          accept=".pdf,.docx,.pptx"
                          onChange={e => setNewPostFile(e.target.files[0] || null)}
                          style={{ fontSize: "12px", marginBottom: "12px" }}
                        />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                          <button onClick={() => { setShowNewPostForm(false); setNewPostTitle(""); setNewPostFile(null); }}
                            style={{ padding: "7px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>
                            Cancel
                          </button>
                          <button onClick={submitNewPost} disabled={creatingPost || !newPostTitle.trim()}
                            style={{ padding: "7px 16px", fontSize: "13px", background: creatingPost || !newPostTitle.trim() ? COLORS.bgTertiary : COLORS.blue, color: creatingPost || !newPostTitle.trim() ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: creatingPost || !newPostTitle.trim() ? "default" : "pointer", fontWeight: "500" }}>
                            {creatingPost ? "Posting..." : "Post"}
                          </button>
                        </div>
                      </div>
                    )}

                    {loadingPosts ? (
                      <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading posts...</p>
                    ) : groupPosts.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 20px", background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px" }}>
                        <p style={{ fontSize: "14px", color: COLORS.textSecondary, marginBottom: "4px" }}>No posts yet</p>
                        <p style={{ fontSize: "12px", color: COLORS.textTertiary }}>Start the discussion for this group</p>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {groupPosts.map(p => (
                          <div key={p.id} onClick={() => openPost(p.id)}
                            style={{ padding: "14px 16px", background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "9px", cursor: "pointer" }}>
                            <div style={{ fontSize: "14px", fontWeight: "500", color: COLORS.textPrimary, marginBottom: "4px" }}>{p.title}</div>
                            <div style={{ fontSize: "12px", color: COLORS.textTertiary }}>
                              {p.author} · {new Date(p.created_at).toLocaleDateString()} · {p.comment_count} comment{p.comment_count !== 1 ? "s" : ""}
                              {p.file && ` · 📄 ${p.file.filename}${p.file.is_version ? ` (v${p.file.version_number})` : ""}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div ref={threadRef}>
                    <div onClick={backToFeed} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "14px", cursor: "pointer", fontSize: "13px", color: COLORS.blue }}>
                      ← Back to Discussion
                    </div>

                    {loadingPostDetail || !postDetail ? (
                      <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading thread...</p>
                    ) : (
                      <>
                        {/* Original post */}
                        <div id="post-original" style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
                          <div style={{ fontSize: "16px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "6px" }}>{postDetail.title}</div>
                          <div style={{ fontSize: "12px", color: COLORS.textTertiary, marginBottom: postDetail.file ? "10px" : 0 }}>
                            {postDetail.author} · {new Date(postDetail.created_at).toLocaleDateString()}
                          </div>
                          {postDetail.file && (
                            <div onClick={() => setPreviewFile(postDetail.file)}
                              style={{ display: "inline-block", padding: "6px 12px", borderRadius: "8px", background: COLORS.blueLight, fontSize: "12px", color: COLORS.blue, cursor: "pointer" }}>
                              📄 {postDetail.file.filename}{postDetail.file.is_version ? ` (v${postDetail.file.version_number})` : ""}
                            </div>
                          )}
                        </div>

                        {/* Comments */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                          {postDetail.comments.length === 0 ? (
                            <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>No comments yet — start the discussion.</p>
                          ) : (
                            postDetail.comments.map(c => (
                              <div key={c.id} id={`comment-${c.id}`} style={{ padding: "12px 14px", background: COLORS.bgSecondary, borderRadius: "9px" }}>
                                <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "4px" }}>
                                  {c.author} <span style={{ fontWeight: "400", color: COLORS.textTertiary }}>· {new Date(c.created_at).toLocaleDateString()}</span>
                                </div>
                                {c.content && <div style={{ fontSize: "13px", color: COLORS.textPrimary, marginBottom: c.file ? "8px" : 0 }}>{c.content}</div>}
                                {c.file && (
                                  <div onClick={() => setPreviewFile(c.file)}
                                    style={{ display: "inline-block", padding: "5px 10px", borderRadius: "7px", background: COLORS.blueLight, fontSize: "12px", color: COLORS.blue, cursor: "pointer" }}>
                                    📄 {c.file.filename}{c.file.is_version ? ` (v${c.file.version_number})` : ""}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>

                        {/* Comment composer */}
                        {commentError && (
                          <div style={{ marginBottom: "10px", padding: "8px 12px", background: COLORS.dangerBg, border: `0.5px solid ${COLORS.dangerBorder}`, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "12px", color: COLORS.danger }}>{commentError}</span>
                            <span onClick={() => setCommentError("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.danger }}>✕</span>
                          </div>
                        )}
                        <div style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px", padding: "14px" }}>
                          <textarea
                            placeholder="Write a comment..."
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            rows={3}
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "7px", border: `0.5px solid ${COLORS.border}`, fontSize: "13px", outline: "none", boxSizing: "border-box", marginBottom: "10px", resize: "vertical", fontFamily: "inherit" }}
                          />
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                            <input
                              type="file"
                              accept=".pdf,.docx,.pptx"
                              onChange={e => setCommentFile(e.target.files[0] || null)}
                              style={{ fontSize: "12px" }}
                            />
                            {commentFile && filesInThread().length > 0 && (
                              <select value={commentVersionOf} onChange={e => setCommentVersionOf(e.target.value)}
                                style={{ padding: "5px 8px", borderRadius: "6px", border: `0.5px solid ${COLORS.border}`, fontSize: "12px", outline: "none", background: COLORS.bg, color: COLORS.textSecondary }}>
                                <option value="">Upload as new file</option>
                                {filesInThread().map(f => (
                                  <option key={f.id} value={f.id}>New version of: {f.filename}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={submitComment} disabled={addingComment || (!commentText.trim() && !commentFile)}
                              style={{ padding: "8px 18px", fontSize: "13px", background: addingComment || (!commentText.trim() && !commentFile) ? COLORS.bgTertiary : COLORS.blue, color: addingComment || (!commentText.trim() && !commentFile) ? COLORS.textTertiary : "white", border: "none", borderRadius: "7px", cursor: addingComment || (!commentText.trim() && !commentFile) ? "default" : "pointer", fontWeight: "500" }}>
                              {addingComment ? "Posting..." : "Comment"}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              )}

              {activeTab === "files" && (
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>

                  {/* Folder sidebar */}
                  <div style={{ width: "180px", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                      <span style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary }}>FOLDERS</span>
                      <span onClick={() => setShowNewWallFolderForm(true)} title="New folder" style={{ cursor: "pointer", fontSize: "14px", color: COLORS.blue }}>+</span>
                    </div>
                    <div onClick={() => setActiveWallFolderId(null)}
                      style={{ padding: "6px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "12px", marginBottom: "2px", background: activeWallFolderId === null ? COLORS.blueLight : "transparent", color: activeWallFolderId === null ? COLORS.blue : COLORS.textPrimary }}>
                      🗂 All Files ({sharedFiles.length})
                    </div>
                    {flatWallFolders.map(f => {
                      const count = sharedFiles.filter(sf => sf.folder_id === f.id).length;
                      return (
                        <div key={f.id} onClick={() => setActiveWallFolderId(f.id)}
                          style={{ padding: "6px 10px", paddingLeft: `${10 + f.depth * 12}px`, borderRadius: "6px", cursor: "pointer", fontSize: "12px", marginBottom: "2px", background: activeWallFolderId === f.id ? COLORS.blueLight : "transparent", color: activeWallFolderId === f.id ? COLORS.blue : COLORS.textPrimary }}>
                          📁 {f.name} ({count})
                        </div>
                      );
                    })}
                    <button onClick={() => handleDownloadSharedZip(activeGroup.id, activeWallFolderId, activeWallFolderName)} disabled={downloadingSharedZip || visibleWallFiles.length === 0}
                      style={{ width: "100%", marginTop: "8px", padding: "6px 8px", fontSize: "11px", background: downloadingSharedZip || visibleWallFiles.length === 0 ? COLORS.bgTertiary : COLORS.blue, color: downloadingSharedZip || visibleWallFiles.length === 0 ? COLORS.textTertiary : "white", border: "none", borderRadius: "6px", cursor: downloadingSharedZip || visibleWallFiles.length === 0 ? "default" : "pointer", fontWeight: "500" }}>
                      {downloadingSharedZip ? "Zipping…" : `⬇️ Download all (${visibleWallFiles.length})`}
                    </button>
                    {showNewWallFolderForm && (
                      <div style={{ marginTop: "8px" }}>
                        <input
                          type="text"
                          autoFocus
                          placeholder="Folder name"
                          value={newWallFolderName}
                          onChange={e => setNewWallFolderName(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && submitNewWallFolder()}
                          onBlur={() => { if (!newWallFolderName.trim()) setShowNewWallFolderForm(false); }}
                          style={{ width: "100%", padding: "5px 8px", borderRadius: "6px", border: `0.5px solid ${COLORS.border}`, fontSize: "12px", outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                    )}
                  </div>

                  {/* File grid */}
                  <div style={{ flex: 1 }}>
                    {sharedFilesMessage && (
                      <div style={{ marginBottom: "12px", padding: "8px 12px", background: COLORS.blueLight, border: `0.5px solid ${COLORS.blueMid}`, borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: "12px", color: COLORS.blue }}>{sharedFilesMessage}</span>
                        <span onClick={() => setSharedFilesMessage("")} style={{ cursor: "pointer", fontSize: "13px", color: COLORS.blue }}>✕</span>
                      </div>
                    )}

                    {loadingSharedFiles ? (
                      <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading shared files...</p>
                    ) : visibleWallFiles.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 20px", background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "10px" }}>
                        <p style={{ fontSize: "14px", color: COLORS.textSecondary, marginBottom: "4px" }}>No shared files here yet</p>
                        <p style={{ fontSize: "12px", color: COLORS.textTertiary }}>Files attached to posts and comments will appear here automatically</p>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {visibleWallFiles.map(f => (
                          <div key={f.id} onContextMenu={e => { e.preventDefault(); setSharedFileContextMenu({ x: e.clientX, y: e.clientY, file: f }); }}
                            style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "9px" }}>
                            <div onClick={() => setPreviewFile(f)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                              <div style={{ fontSize: "13px", fontWeight: "500", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                📄 {f.filename}{f.version_number > 1 ? ` (v${f.version_number})` : ""}
                                {summarizingSharedFileIds.includes(f.id) && <span style={{ color: COLORS.blue, fontWeight: "400" }}> · ✨ Summarizing…</span>}
                              </div>
                              <div style={{ fontSize: "11px", color: COLORS.textTertiary }}>
                                {f.tag} · from "{f.post_title}" · {f.uploaded_by} · {new Date(f.uploaded_at).toLocaleDateString()}
                              </div>
                              {f.summary && (
                                <div
                                  onClick={e => { e.stopPropagation(); setExpandedSharedSummaries(prev => ({ ...prev, [f.id]: !prev[f.id] })); }}
                                  title={expandedSharedSummaries[f.id] ? "Click to collapse" : "Click to read full summary"}
                                  style={{
                                    fontSize: "11px", color: COLORS.textSecondary, marginTop: "2px", cursor: "pointer", wordBreak: "break-word",
                                    ...(expandedSharedSummaries[f.id]
                                      ? {}
                                      : { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" })
                                  }}
                                >
                                  {f.summary}
                                </div>
                              )}
                            </div>
                            <span onClick={e => { e.stopPropagation(); jumpToFileInThread(f); }} title="View in thread"
                              style={{ cursor: "pointer", fontSize: "14px", color: COLORS.blue, flexShrink: 0 }}>
                              💬
                            </span>
                            <select value={f.folder_id || ""} onChange={e => handleAssignSharedFileFolder(activeGroup.id, f.id, e.target.value ? parseInt(e.target.value) : null)}
                              style={{ padding: "5px 8px", borderRadius: "6px", border: `0.5px solid ${COLORS.border}`, fontSize: "12px", outline: "none", background: COLORS.bg, color: COLORS.textSecondary, flexShrink: 0 }}>
                              <option value="">No folder</option>
                              {flatWallFolders.map(folder => (
                                <option key={folder.id} value={folder.id}>{"—".repeat(folder.depth)} {folder.name}</option>
                              ))}
                            </select>
                            <button onClick={() => handleCopyToMyFiles(f.id, f.filename)} disabled={copyingFileId === f.id}
                              style={{ padding: "6px 12px", fontSize: "12px", background: copyingFileId === f.id ? COLORS.bgTertiary : "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "6px", color: copyingFileId === f.id ? COLORS.textTertiary : COLORS.textSecondary, cursor: copyingFileId === f.id ? "default" : "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
                              {copyingFileId === f.id ? "Copying…" : "Copy to My Files"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {sharedFileContextMenu && (
                      <>
                        <div onClick={() => setSharedFileContextMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                        <div style={{ position: "fixed", top: sharedFileContextMenu.y, left: sharedFileContextMenu.x, background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: "160px", padding: "4px 0" }} onClick={e => e.stopPropagation()}>
                          <div onClick={() => { window.open(`${API}/files/${sharedFileContextMenu.file.id}/download?token=${token}`); setSharedFileContextMenu(null); }}
                            style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                            ⬇️ Download
                          </div>
                          <div onClick={() => { if (!summarizingSharedFileIds.includes(sharedFileContextMenu.file.id)) { handleSummarizeSharedFile(activeGroup.id, sharedFileContextMenu.file.id); setSharedFileContextMenu(null); } }}
                            style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>
                            ✨ Summarize
                          </div>
                          {isAdmin && (
                            <>
                              <div style={{ borderTop: `0.5px solid ${COLORS.borderLight}`, margin: "4px 0" }} />
                              <div onClick={() => { handleDeleteSharedFile(activeGroup.id, sharedFileContextMenu.file.id, sharedFileContextMenu.file.filename); setSharedFileContextMenu(null); }}
                                style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.danger }}>
                                🗑 Delete{deletingSharedFileId === sharedFileContextMenu.file.id ? "…" : ""}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <FilePreviewModal previewFile={previewFile} setPreviewFile={setPreviewFile} token={token} />
    </div>
  );
}